<template lang="pug">
.author-home.flex.flex-col.h-screen.w-screen.bg-white.dark_bg-gray-700
  .flex.gap-2.p-2.z-50.bg-gray-100.dark_bg-gray-800.h-14.items-center
    MainMenu
      .flex.flex-col
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-audio"
        )
          | Bulk Audio
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-customisations"
        )
          | Bulk Customisations
        a.text-gray-900.group.rounded-md.p-2.text-sm.hover_bg-primary-100(
          href="#/bulk-translation-jobs"
        )
          | Bulk Translation Jobs
    .ms-auto
    ToggleDark
    button.btn.btn-primary.px-5.py-2.bg-primary-600.text-white.btn-hover_bg-opacity-90.active_bg-opacity-80(
      @click="openNewCourseDrawer"
      type="button"
    ) New ✏️
    button.btn.btn-primary.px-5.py-2.bg-primary-600.text-white.btn-hover_bg-opacity-90.active_bg-opacity-80.disabled_opacity-50.disabled_bg-gray-600(
      @click="openCheckCourseDrawer"
      type="button"
      :disabled="isOpenCheckCourseDrawer || activityName === 'Conversational Learning'"
    ) Course Tests 🧪

  .flex-1.p-10.overflow-y-auto 
    .max-w-screen-2xl.mx-auto
      transition-expand
        div(v-if="isOpenNewCourseDrawer")
          NewCourseDrawer.mx-auto(@close="closeNewCourseDrawer" :defaultActivityName="activityName")
        div(v-if="isOpenCheckCourseDrawer")
          CourseTestsDrawer.mx-auto(@close="closeCheckCourseDrawer" :selectedCourseIds="selectedCourseIds" :maxSelected="maxSelected" @clearSelectedCourses="clearSelectedCourses")

      .mx-auto.text-center.prose.prose-sm.dark_prose-invert(v-if="loadingError")
        p We couldn't get the courses 😓
        p.italic {{ loadingError }}
        button.my-4.text-sm.btn.bg-gray-300(
          @click="loadCourses"
          type="button"
        ) Try again

      .mx-auto.text-center.prose.prose-sm.dark_prose-invert.italic.animate-loading(v-else-if="!courses?.[0] || advancedSearchStatus === 'pending'")
        p Loading courses

      form.flex.flex-col.gap-4.items-center.mt-8.mb-4(v-else)

        div.border-2.border-dashed.border-gray-500.rounded-xl.p-2.flex.flex-col.gap-2.items-center
          .prose.prose-2xl.flex.flex-col.gap-2.items-center
            div(
              :class="['w-[30em]', { 'flex gap-2': showAdvancedSearch }]"
            )
              span.relative.flex-1
                input.w-full.dark_bg-white.dark_text-gray-800.dark_transition.px-6.py-2(placeholder="Filter or choose advanced search" type="text" v-model="searchTerm" @input="advancedSearchStatus = 'idle'")
                Icon(icon="ph:magnifying-glass" class="absolute top-1/2 transform -translate-y-1/2 right-4 text-gray-400 dark_text-gray-600 pointer-events-none")
              button.btn.btn-primary.inline-block.px-5.py-2.bg-primary-600.text-white.btn-hover_bg-opacity-90.active_bg-opacity-80(
                @click="advancedSearch"
                type="submit"
                v-if="showAdvancedSearch"
              ) Search

          .prose.prose-sm.dark_prose-invert.flex.flex-col.gap-4
            //- Activity type selection
            .inline-flex.gap-2
              label.inline-flex.items-center.gap-1
                input(type="radio" name="activityName" value="Full Course" v-model="activityName")
                span Full Course
              label.inline-flex.items-center.gap-1
                input(type="radio" name="activityName" value="Conversational Learning" v-model="activityName")
                span Conversational Learning
              label.inline-flex.items-center.gap-1(v-if="!showAdvancedSearch")
                input(type="radio" name="activityName" value="Resource" v-model="activityName")
                span Resource

            //- Advanced search options (hidden for resources)
            .inline-flex.gap-2.items-center(v-if="activityName !== 'Resource'")
              label.inline-flex.items-center.gap-1
                input(type="checkbox" v-model="showAdvancedSearch")
                span Advanced search
              div.inline-flex.gap-2.ml-auto(v-if="showAdvancedSearch")
                label.inline-flex.items-center.gap-1
                  input(type="radio" name="searchContentType" value="yaml" v-model="searchContentType")
                  span YAML
                label.inline-flex.items-center.gap-1
                  input(type="radio" name="searchContentType" value="css" v-model="searchContentType")
                  span CSS

        .prose.prose-sm.dark_prose-invert
          //- Resource type filters
          .inline-flex.gap-2(v-if="activityName === 'Resource'")
            span Filter:
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeBlogs" value="all" v-model="includeBlogs")
              span Blogs
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeGuides" value="all" v-model="includeGuides")
              span Guides
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeTemplates" value="all" v-model="includeTemplates")
              span Templates
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeVideos" value="all" v-model="includeVideos")
              span Videos
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeWebinars" value="all" v-model="includeWebinars")
              span Webinars
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includePlaylists" value="all" v-model="includePlaylists")
              span Playlists
          //- Course type filters
          .inline-flex.gap-2(v-else)
            span Filter:
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeBase" value="all" v-model="includeBase")
              span Base courses
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeCustom" value="all" v-model="includeCustom")
              span Customisations
            label.inline-flex.items-center.gap-1
              input(type="checkbox" name="includeBespoke" value="all" v-model="includeBespoke")
              span Bespoke courses
            label.inline-flex.items-center.gap-1 
              input(type="checkbox" name="includeRetired" value="all" v-model="includeRetired")
              span Retired courses

        .flex.gap-2.items-center.justify-center.text-sm.text-gray-600.dark_text-gray-200.italic
          span Results: {{ filteredCourses.length }}.
          span(v-if="filteredCourses.length > 200") Showing: {{ filteredCourses.slice(0, 200).length }} results.

      //- Advanced search results with context
      div(v-if="showAdvancedSearch && advancedSearchStatus === 'success' && filteredAdvancedSearchResults.length > 0")
        .space-y-4
          .prose.prose-sm.dark_prose-invert.mb-4
            p Results: {{ filteredSearchResultsTotalMatches }} matches found.

          .space-y-6
            .border-2.border-gray-400.dark_border-gray-600.rounded-lg.p-5.bg-gray-50.dark_bg-gray-800.shadow-md.hover_shadow-lg.transition-shadow.bg-primary-100(
              v-for="result in filteredAdvancedSearchResults"
              :key="`${result.firmCode || ''}_${result.courseId || result.vinciResourceId}`"
              :class="'dark_bg-primary-900/30', { 'primary-orange': result.type === 'course', 'primary-teal': result.type === 'custom', 'primary-indigo': result.type === 'bespoke', 'primary-coolgray': isCourseRetired(result) }"
            )
              //- Course info section (similar to course card)
              .flex.flex-col.gap-3.mb-4.pb-4.border-b-2.border-gray-300.dark_border-gray-700
                .flex.items-start.justify-between.gap-2
                  div.flex-1
                    a.text-xl.font-semibold.text-primary-700.dark_text-primary-200.hover_text-primary-900.dark_hover_text-primary-400(
                      :href="result.type === 'resource' ? '#' : `#/editor/${getCourseName(result)}`"
                      @click="handleResourceClick($event, result)"
                    ) {{ getCourseName(result) }}
                      span.text-sm.text-gray-500.ml-1(v-if="isCourseRetired(result)") (retired)
                    h3.text-2xl.font-bold.text-gray-900.dark_text-white.mt-1.mb-2
                      span(v-if="result.title") {{ result.title }}
                      span.opacity-40(v-else) [Unknown title]

                    .flex.flex-wrap.gap-4.text-base.text-gray-700.dark_text-gray-300.mt-2
                      div.font-medium {{ getTypeLabel(result) }}
                      div Last saved #[b {{ computeSavedTimeAgo(result) }}]
                      div {{ computeSavedDateString(result) }}
                      div by #[b {{ result.author }}]

                  .flex.gap-1(v-if="result.type !== 'resource'")
                    template(v-if="(result.activityName || 'Full Course') === 'Full Course'")
                      a.group.text-primary-950.dark_text-primary-200.hover_text-primary-700.dark_hover_text-primary-800.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/audio/${getCourseName(result)}`" title="Audio")
                        Icon.opacity-70.group-hover_opacity-100(icon="ph:speaker-high")
                        span.sr-only Audio
                      a.group.text-primary-950.dark_text-primary-200.hover_text-primary-700.dark_hover_text-primary-800.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/translate/${getCourseName(result)}`" title="Translate")
                        Icon.opacity-70.group-hover_opacity-100(icon="ph:translate")
                        span.sr-only Translate
                      a.group.text-primary-950.dark_text-primary-200.hover_text-primary-700.dark_hover_text-primary-800.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/transcript/${getCourseName(result)}`" title="Transcript")
                        Icon.opacity-70.group-hover_opacity-100(icon="ph:file-pdf")
                        span.sr-only Transcript
                    a.group.text-primary-950.dark_text-primary-200.hover_text-primary-700.dark_hover_text-primary-800.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/history/${getCourseName(result)}`" title="History")
                      Icon.opacity-70.group-hover_opacity-100(icon="ph:clock-counter-clockwise")
                      span.sr-only History
                    a.group.text-primary-950.dark_text-primary-200.hover_text-primary-700.dark_hover_text-primary-800.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/editor/${getCourseName(result)}`" title="Edit")
                      Icon.opacity-70.group-hover_opacity-100(icon="ph:pencil")
                      span.sr-only Edit

              //- Match contexts section
              div(v-if="result.context && result.context.length > 0")
                .text-base.font-semibold.text-gray-800.dark_text-gray-300.mb-3
                  | Matches ({{ result.context.length }}):
                .space-y-3
                  .bg-white.dark_bg-gray-900.rounded-md.p-4.border-l-4.border-primary-600.dark_border-primary-500.shadow-sm(
                    v-for="(match, index) in result.context"
                    :key="index"
                  )
                    .flex.items-center.gap-4.mb-2
                      .flex-shrink-0
                        span.inline-block.px-2.py-1.text-sm.font-mono.font-semibold.bg-primary-200.dark_bg-primary-900.text-primary-800.dark_text-primary-300.rounded.border.border-primary-400.dark_border-primary-700
                          | Line {{ match.lineNumber }}
                      .prop
                        span.text-base.font-medium.text-gray-800.dark_text-gray-300
                          | Property: 
                          code.text-primary-700.dark_text-primary-400.font-mono.bg-primary-50.dark_bg-primary-950.px-1.rounded {{ match.property }}
                      .path
                        span.text-base.font-medium.text-gray-800.dark_text-gray-300(v-if="match.path")
                          | Path: 
                          code.text-primary-700.dark_text-primary-400.font-mono.bg-primary-50.dark_bg-primary-950.px-1.rounded {{ match.path }}
                    .mt-2
                      pre.text-sm.font-mono.bg-gray-100.dark_bg-gray-950.p-3.rounded.overflow-x-auto.text-gray-900.dark_text-gray-200.whitespace-pre-wrap.border.border-gray-300.dark_border-gray-700(
                        v-html="highlightSearchTerm(match.value, searchTerm)"
                      )
              div(v-else)
                .text-base.text-gray-600.dark_text-gray-400.italic No context available

      div(v-else-if="courses[0] && !filteredCourses?.[0]")
        .mx-auto.text-center.prose.prose-xl.dark_prose-invert.italic.w-full
          p.mx-auto.text-center
            span(v-if="!showAdvancedSearch || advancedSearchStatus === 'idle'") No results.
            span(v-if="showAdvancedSearch && advancedSearchStatus === 'idle'") Please search for some content.
            span(v-if="showAdvancedSearch && advancedSearchStatus === 'success' && filteredAdvancedSearchResults.length === 0") No results.
            span.text-red-500.font-bold(v-if="showAdvancedSearch && advancedSearchStatus === 'error'") Search error!

      .grid.grid-cols-1.sm_grid-cols-2.md_grid-cols-3.lg_grid-cols-4.gap-4(v-else-if="!showAdvancedSearch || advancedSearchStatus !== 'success'")
        .p-3.rounded-md.prose.group.hover_brightness-105.bg-primary-100.dark_bg-primary-200.transition.relative.flex.flex-col.gap-2(
          class="group/card"
          :key="course.id"
          v-for="course in filteredCourses.slice(0, 200)"
          :class="{ 'primary-orange': course.type === 'course', 'primary-teal': course.type === 'custom', 'primary-indigo': course.type === 'bespoke', 'primary-blue': course.type === 'resource', 'primary-coolgray': isCourseRetired(course) }"  
        ) 
          p.flex.flex-col-reverse.gap-2.not-prose.z-20.m-0.mb-4
            a.text-current.hover_text-primary-700.group-hover_text-primary-700.overflow-hidden.text-ellipsis(
              :href="course.type === 'resource' ? '#' : `#/editor/${getCourseName(course)}`"
              :title="getCourseName(course)"
              @click="handleResourceClick($event, course)"
            ) {{ getCourseName(course) }}
              span.text-sm.text-gray-500.ml-1(v-if="isCourseRetired(course)") (retired)

            .flex.justify-end.gap-1(v-if="course.type !== 'resource'")
              template(v-if="(course.activityName || 'Full Course') === 'Full Course'")
                a.group.text-primary-950.hover_text-primary-700.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/audio/${getCourseName(course)}`" title="Audio")
                  Icon.opacity-70.group-hover_opacity-100(icon="ph:speaker-high")
                  span.sr-only Audio

                a.group.text-primary-950.hover_text-primary-700.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/translate/${getCourseName(course)}`" title="Translate")
                  Icon.opacity-70.group-hover_opacity-100(icon="ph:translate")
                  span.sr-only Translate

                a.group.text-primary-950.hover_text-primary-700.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/transcript/${getCourseName(course)}`" title="Transcript")
                  Icon.opacity-70.group-hover_opacity-100(icon="ph:file-pdf")
                  span.sr-only Transcript

              a.group.text-primary-950.hover_text-primary-700.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/history/${getCourseName(course)}`" title="History")
                Icon.opacity-70.group-hover_opacity-100(icon="ph:clock-counter-clockwise")
                span.sr-only History

              a.group.text-primary-950.hover_text-primary-700.text-2xl.py-2.px-1.opacity-70.hover_opacity-100.hover_bg-primary-200.rounded-lg.bg-opacity-50(:href="`#/editor/${getCourseName(course)}`" title="Edit")
                Icon.opacity-70.group-hover_opacity-100(icon="ph:pencil")
                span.sr-only Edit
          .flex-1
            h2.text-current.m-0.not-prose
              a.peer.absolute.inset-0.m-auto(
                :href="course.type === 'resource' ? '#' : `#/editor/${getCourseName(course)}`"
                @click="handleResourceClick($event, course)"
              ) 
                span.sr-only Edit
              a.text-current.peer-hover_text-primary-700(
                :href="course.type === 'resource' ? '#' : `#/editor/${getCourseName(course)}`"
                @click="handleResourceClick($event, course)"
              ) 
                span(v-if="course.title") {{ course.title }}
                span.opacity-40(v-else="") [Unknown title]
          p.text-sm
            div {{ getTypeLabel(course) }} 
            div Last saved #[b {{ computeSavedTimeAgo(course) }}]
            div {{ computeSavedDateString(course) }}
            div by #[b {{ course.author }}]

          div.group.relative.z-20(v-if="isOpenCheckCourseDrawer && activityName !== 'Conversational Learning'")  
            label.inline-flex.items-center.gap-1
              input(
                type="checkbox"
                :value="getCourseName(course)"
                v-model="includeInCheck"
                :disabled="includeInCheck.length >= maxSelected && !includeInCheck.includes(getCourseName(course))"
                :class="{ 'opacity-50': includeInCheck.length >= maxSelected && !includeInCheck.includes(getCourseName(course)) }"
              )
              span(:class="{ 'opacity-50': includeInCheck.length >= maxSelected && !includeInCheck.includes(getCourseName(course)) }") Include in check

</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import MainMenu from './MainMenu.vue';
import { listSaved } from '../lib/callModContentAuthors';
import { formatTimeAgo } from '@vueuse/core';
import TransitionExpand from './TransitionExpand.vue';
import NewCourseDrawer from './NewCourseDrawer.vue';
import ToggleDark from './ToggleDark.vue';
import Fuse from 'fuse.js';
import { Icon } from '@iconify/vue';
import useDocTitle from '../lib/useDocTitle';
import { getRecentlySaved } from '../store/recentlySaved';
import { searchCourses } from '../../lib/callModContent';
import {
  searchCoursesWithContext,
  getAllCoursesFromLibrary
} from '../lib/callModContentAuthors';
import CourseTestsDrawer from './CourseTestsDrawer.vue';

type SearchMatchContext = {
  property: string;
  value: string;
  lineNumber: number;
};

type CourseWithSearchContext = CourseCompactResponse & {
  yaml?: string;
  css?: string;
  context: SearchMatchContext[];
};

useDocTitle('Home');

const router = useRouter();

const getCourseName = (course: CourseCompactResponse) => {
  // Resources use vinciResourceId instead of courseId
  const id = (course as any).vinciResourceId || course.courseId;
  return course.firmCode ? `${course.firmCode}_${id}` : `${id}`;
};

// Function to highlight search terms in the match value
const highlightSearchTerm = (text: string, searchTerm: string): string => {
  if (!searchTerm || !text) return text;

  // Escape special regex characters in the search term
  const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Create a regex that matches the search term case-insensitively
  const regex = new RegExp(`(${escapedSearch})`, 'gi');

  // Replace matches with highlighted spans
  return text.replace(
    regex,
    '<mark class="bg-yellow-400 dark_bg-yellow-500 text-gray-900 dark_text-gray-900 font-semibold px-0.5 rounded">$1</mark>'
  );
};

const isCourseRetired = (course: CourseCompactResponse) => {
  const courseFromLibrary = allCoursesFromLibrary.value.find(
    c => c.vinciResourceId === course.courseId
  );
  return courseFromLibrary?.retired;
};

const searchTerm = ref('');
const activityName = ref('Full Course');
const includeBase = ref(true);
const includeCustom = ref(true);
const includeBespoke = ref(true);
// Resource type filters
const includeBlogs = ref(true);
const includeGuides = ref(true);
const includeTemplates = ref(true);
const includeVideos = ref(true);
const includeWebinars = ref(true);
const includePlaylists = ref(true);
const includeRetired = ref(false);
const showAdvancedSearch = ref(false);
const searchContentType = ref('yaml');
const advancedSearchStatus = ref('idle');
const advancedSearchResults = ref<CourseCompactResponse[]>([]);
const advancedSearchResultsWithContext = ref<CourseWithSearchContext[]>([]);
const devOrQA =
  window.location.hostname.includes('-dev') ||
  window.location.hostname.includes('-qa');

watch(showAdvancedSearch, () => {
  if (!showAdvancedSearch.value) {
    advancedSearchResults.value = [];
    advancedSearchResultsWithContext.value = [];
  }
});

const fuse = computed(
  () =>
    new Fuse(
      courses.value.map(c => {
        // Use the appropriate ID for the combined search string
        const id = (c as any).vinciResourceId || c.courseId;
        return {
          ...c,
          _combined: `${c.firmCode}_${id} ${c.title} ${c.author}`,
          _searchId: id // Add a searchable ID field
        };
      }),
      {
        keys: [
          'courseId',
          'vinciResourceId',
          'title',
          'firmCode',
          '_combined',
          '_searchId'
        ],
        threshold: 0.3
      }
    )
);

// get all courses from library using getAllCoursesFromLibrary so we can filter on retired status
const allCoursesFromLibrary = ref<any[]>([]);
const loadAllCoursesFromLibrary = async () => {
  const response = await getAllCoursesFromLibrary({ type: 'course' });
  allCoursesFromLibrary.value = response.data?.data?.results || [];
};

const advancedSearch = async () => {
  advancedSearchStatus.value = 'pending';
  try {
    const results = await searchCoursesWithContext(
      searchTerm.value,
      searchContentType.value as 'yaml' | 'css'
    );
    advancedSearchStatus.value = 'success';
    advancedSearchResultsWithContext.value = results || [];
    // Also store compact results for filtering
    advancedSearchResults.value = results.map(
      ({ yaml, css, ...rest }) => rest
    ) as CourseCompactResponse[];
  } catch (e) {
    advancedSearchStatus.value = 'error';
    console.error('Advanced search error:', e);
  }
};

const searchResult = computed(() => {
  return showAdvancedSearch.value && advancedSearchStatus.value === 'success'
    ? advancedSearchResults.value
    : !showAdvancedSearch.value && searchTerm.value
      ? fuse.value.search(searchTerm.value).map(({ item }) => item)
      : !showAdvancedSearch.value
        ? courses.value
        : [];
});

const filteredCourses = computed(() => {
  return searchResult.value?.filter(course => {
    if (course.firmCode?.includes('vwptestcompany') && !devOrQA) return false;

    // Resource filtering
    if (activityName.value === 'Resource') {
      if (course.type !== 'resource') return false;
      // Filter by resource type
      const resourceType = course.resourceType;
      if (resourceType === 'Blog') return includeBlogs.value;
      if (resourceType === 'Guide') return includeGuides.value;
      if (resourceType === 'Template') return includeTemplates.value;
      if (resourceType === 'Video') return includeVideos.value;
      if (resourceType === 'Webinar') return includeWebinars.value;
      if (resourceType === 'Playlist') return includePlaylists.value;
      return true; // Show resources without a type when all filters checked
    } else {
      // Course filtering - exclude resources
      if (course.type === 'resource') return false;

      // Product filtering - Handle legacy courses without product property
      const courseProduct = course.activityName || 'Full Course';

      // Filter by selected activity type
      if (
        activityName.value === 'Full Course' &&
        courseProduct === 'Conversational Learning'
      )
        return false;
      if (
        activityName.value === 'Conversational Learning' &&
        courseProduct !== 'Conversational Learning'
      )
        return false;

      // Filter by retired status using allCoursesFromLibrary. Match vinciResourceId to courseId.
      const courseFromLibrary = allCoursesFromLibrary.value.find(
        c => c.vinciResourceId === course.courseId
      );
      if (courseFromLibrary?.retired) return includeRetired.value;

      if (course.type === 'course') return includeBase.value;
      if (course.type === 'custom') return includeCustom.value;
      if (course.type === 'bespoke') return includeBespoke.value;
      return true;
    }
  });
});

const filteredAdvancedSearchResults = computed(() => {
  return advancedSearchResultsWithContext.value?.filter(result => {
    if (result.firmCode?.includes('vwptestcompany') && !devOrQA) return false;

    // Resource filtering
    if (activityName.value === 'Resource') {
      if (result.type !== 'resource') return false;
      // Filter by resource type
      const resourceType = result.resourceType;
      if (resourceType === 'Blog') return includeBlogs.value;
      if (resourceType === 'Guide') return includeGuides.value;
      if (resourceType === 'Template') return includeTemplates.value;
      if (resourceType === 'Video') return includeVideos.value;
      if (resourceType === 'Webinar') return includeWebinars.value;
      if (resourceType === 'Playlist') return includePlaylists.value;
      return true; // Show resources without a type when all filters checked
    } else {
      // Course filtering - exclude resources
      if (result.type === 'resource') return false;

      // Product filtering - Handle legacy courses without product property
      const courseProduct = result.activityName || 'Full Course';

      // Filter by selected activity type
      if (
        activityName.value === 'Full Course' &&
        courseProduct === 'Conversational Learning'
      )
        return false;
      if (
        activityName.value === 'Conversational Learning' &&
        courseProduct !== 'Conversational Learning'
      )
        return false;

      // Filter by retired status
      const courseFromLibrary = allCoursesFromLibrary.value.find(
        c => c.vinciResourceId === result.courseId
      );
      if (courseFromLibrary?.retired) return includeRetired.value;

      if (result.type === 'course') return includeBase.value;
      if (result.type === 'custom') return includeCustom.value;
      if (result.type === 'bespoke') return includeBespoke.value;
      return true;
    }
  });
});

const filteredSearchResultsTotalMatches = computed(() => {
  return filteredAdvancedSearchResults.value?.reduce((acc, result) => {
    switch (result.type) {
      case 'course':
      case 'custom':
      case 'bespoke':
        return acc + result.context.length;
      default:
        return acc;
    }
  }, 0);
});

const myRecentCourses = computed(() => {
  const saved = getRecentlySaved();

  return saved
    .map(({ courseId, firmCode }) => {
      const course = courses.value.find(
        c => c.courseId === courseId && c.firmCode === firmCode
      );

      if (!course) return null;

      return course;
    })
    .filter(Boolean);
});

const getTypeLabel = (course: CourseCompactResponse) => {
  if (course.type === 'course') return 'Base course';
  if (course.type === 'custom') return 'Customisation';
  if (course.type === 'bespoke') return 'Bespoke course';
  if (course.type === 'resource') return course.resourceType || 'Resource';
  return 'Unknown type';
};

const isOpenNewCourseDrawer = ref(false);

const openNewCourseDrawer = () => {
  isOpenNewCourseDrawer.value = true;
};

const closeNewCourseDrawer = () => {
  isOpenNewCourseDrawer.value = false;
};

console.log('home');

const courses = ref<CourseCompactResponse[]>([]);

const computeSavedDate = (course: CourseCompactResponse) => {
  const updated = course.metadata?.createdDate;
  if (!updated) return '';

  return new Date(+updated);
};

const computeSavedDateString = (course: CourseCompactResponse) => {
  const date = computeSavedDate(course);
  return (
    date &&
    date.toLocaleString('en-GB', { dateStyle: 'full', timeStyle: 'short' })
  );
};

const computeSavedTimeAgo = (course: CourseCompactResponse) => {
  const date = computeSavedDate(course);
  return date && formatTimeAgo(date);
};

const loadingError = ref('');

const loadCourses = async () => {
  loadingError.value = '';

  const rawList: CourseResponse[] = [];

  try {
    const [savedList, draftsList] = await Promise.all([
      listSaved(),
      // TODO listDrafts()
      []
    ]);

    rawList.push(...savedList, ...draftsList);
    await loadAllCoursesFromLibrary();
  } catch (e) {
    console.error(e);
    loadingError.value = e.message;
  }

  const sorted = rawList.sort(
    (a, b) => b.metadata?.createdDate - a.metadata?.createdDate
  );

  courses.value = sorted;
};

const isOpenCheckCourseDrawer = ref(false);

const openCheckCourseDrawer = () => {
  isOpenCheckCourseDrawer.value = true;
};

const closeCheckCourseDrawer = () => {
  isOpenCheckCourseDrawer.value = false;
};

const includeInCheck = ref([]);
const selectedCourseIds = ref([]);

watch(includeInCheck, newVal => {
  console.log('includeInCheck', newVal);
  selectedCourseIds.value = newVal;
});

const clearSelectedCourses = () => {
  selectedCourseIds.value = [];
  includeInCheck.value = [];
};

// Define a shared constant for the maximum number of selected courses
const maxSelected = 12;

const handleResourceClick = (e: Event, course: CourseCompactResponse) => {
  if (course.type === 'resource') {
    e.preventDefault();
    const resourceId = (course as any).vinciResourceId || course.courseId;
    const fileName = course.firmCode
      ? `${course.firmCode}_${resourceId}`
      : `${resourceId}`;

    router.push({
      path: `/resource-metadata/${fileName}`
    });
  }
};

onMounted(loadCourses);
</script>
